import { epochToSeconds, calculateDuration } from './epochUtils';
import { formatSecondsHumanized } from './durationFormat';
import i18n from '@/i18n';

export const EMPTY_DATE_DISPLAY = '—';

function parseDate(date: string | Date | null | undefined): Date | null {
  if (!date) {
    return null;
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (Number.isNaN(dateObj.getTime())) {
      return null;
    }

    return dateObj;
  } catch {
    return null;
  }
}

export function formatAppDate(
  value: string | Date | null | undefined,
  locale: string
): string {
  const dateObj = parseDate(value);
  if (!dateObj) {
    return EMPTY_DATE_DISPLAY;
  }
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
}

export function formatAppDateTime(
  value: string | Date | null | undefined,
  locale: string
): string {
  const dateObj = parseDate(value);
  if (!dateObj) {
    return EMPTY_DATE_DISPLAY;
  }
  const datePart = formatAppDate(dateObj, locale);
  const timePart = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dateObj);
  return `${datePart} - ${timePart}`;
}

export function formatAppDateParts(
  value: string | Date | null | undefined,
  locale: string
): { date: string; time: string } | null {
  const dateObj = parseDate(value);
  if (!dateObj) {
    return null;
  }
  return {
    date: formatAppDate(dateObj, locale),
    time: new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(dateObj),
  };
}

function isValidDuration(duration: number | string | undefined): duration is number | string {
  return duration !== undefined && duration !== null && duration !== '';
}

function parseDurationToSeconds(duration: number | string): number {
  return typeof duration === 'string' ? parseInt(duration) : duration;
}

export const formatCallDuration = (
  duration: number | string | undefined,
  createdEpoch?: number | string,
  endTime?: number,
  isActive: boolean = false,
  nowMs: number = Date.now()
): string => {
  let durationSeconds = 0;

  // Active calls: derive duration from created_epoch so the UI can tick every second.
  // FreeSWITCH's duration field is only a snapshot at fetch time.
  if (
    isActive &&
    createdEpoch !== undefined &&
    createdEpoch !== null &&
    createdEpoch !== ''
  ) {
    const createdSeconds = epochToSeconds(createdEpoch);
    const end = Math.floor(nowMs / 1000);
    durationSeconds = Math.max(0, end - createdSeconds);
  } else if (endTime && createdEpoch) {
    durationSeconds = calculateDuration(createdEpoch, endTime);
  } else if (createdEpoch) {
    const end = endTime ? Math.floor(endTime / 1000) : Math.floor(nowMs / 1000);
    durationSeconds = calculateDuration(createdEpoch, end);
  } else if (isValidDuration(duration)) {
    durationSeconds = parseDurationToSeconds(duration);
  }

  if (durationSeconds <= 0) return i18n.t('common.na');

  return formatSecondsHumanized(durationSeconds);
};
